import localForage from 'localforage';
import { persistReducer } from 'redux-persist';
import { createSelector } from '@reduxjs/toolkit';
import { type RootState } from 'app/store';
import {
	type NotificationGroup,
	type NotificationGroupsState,
	type NotificationGroupsActionTypes,
} from 'app/store/types';

const initialState: NotificationGroupsState = {};

const notificationGroupsReducer = (state = initialState, action: NotificationGroupsActionTypes) => {
	switch (action.type) {
		case 'GET_LICENSE_GROUP_DATA_SUCCESS': {
			const {
				licenseGroupId,
				data: { notificationGroups },
			} = action.payload;

			let updatedGroups;
			if (notificationGroups) {
				// maintain order since order is not returned in that api call
				const groups = { ...state[licenseGroupId]?.byId };
				updatedGroups = Object.keys(notificationGroups).reduce(
					(result: { [key: string]: NotificationGroup }, id) => {
						const existingGroup = groups[id];
						result[id] = existingGroup?.groupOrder
							? {
									...notificationGroups[id],
									groupOrder: existingGroup.groupOrder,
								}
							: notificationGroups[id];
						return result;
					},
					{},
				);
			}

			return {
				...state,
				[licenseGroupId]: {
					...state[licenseGroupId],
					...(updatedGroups && { byId: updatedGroups }),
				},
			};
		}
		case 'GET_NOTIFICATION_GROUP_ORDER_SUCCESS': {
			const { order, licenseGroupId } = action.payload;
			const groups = { ...state[licenseGroupId].byId };

			// order list may not contain all groups
			const existingGroupIds = order.filter((groupId: string) => groups[groupId]);
			const updatedGroups: { [key: string]: NotificationGroup } = {};

			existingGroupIds.forEach((groupId: string, idx: number) => {
				const group = { ...groups[groupId] };
				group.groupOrder = idx + 1;
				updatedGroups[groupId] = group;
			});

			// assign other group order for non existing groups in order array
			const nonOrderedGroups = Object.keys(groups).filter((groupId) => !existingGroupIds.includes(groupId));

			nonOrderedGroups.forEach((groupId, idx) => {
				const group = { ...groups[groupId] };
				group.groupOrder = existingGroupIds.length + idx + 1;
				updatedGroups[groupId] = group;
			});

			return {
				...state,
				[licenseGroupId]: {
					byId: updatedGroups,
				},
			};
		}
		case 'LOGGED_OUT_USER':
		case 'PURGE_STATE':
			return initialState;
		default: {
			return state;
		}
	}
};

export default persistReducer(
	{
		key: 'notificationGroups',
		storage: localForage,
		whitelist: localStorage.getItem('persist') ? undefined : [],
	},
	notificationGroupsReducer,
);

// Selectors

export const getNotificationGroupsById = ({ app: { selectedLicenseGroupId }, notificationGroups }: RootState) =>
	notificationGroups[selectedLicenseGroupId].byId;

export const getNotificationGroups = createSelector([getNotificationGroupsById], (notificationGroupsById) =>
	Object.values(notificationGroupsById),
);

export const getNotificationGroupById = (notificationGroupId: NotificationGroup['id']) => (state: RootState) =>
	getNotificationGroupsById(state)[notificationGroupId];
